/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Dispatch, SetStateAction, useState } from "react";

import styled from "styled-components";

import { Username } from "../../Shared/User/Username";
import { useCurrentUser } from "src/app/useCurrentUser";
import { Plate } from "src/classes/plate/Plate";
import { TD } from "src/components/DrilledView/SidePanel/Sections/Attributes";
import { Alert } from "src/components/UI/Alert/Alert";
import { Button } from "src/components/UI/Buttons/ButtonPrimitives";
import { Modal } from "src/components/UI/Modal/Modal";
import { H2 } from "src/components/UI/Modal/style";

interface Props {
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  plate: Plate;
}

export function PlateSettings({ setModalOpen, plate }: Props) {
  const currentUser = useCurrentUser();
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [cleanupAlertOpen, setCleanupAlertOpen] = useState(false);

  const currentUserIsOwner = plate.snapshot.currentUserIsOwner;
  const isPublic = plate.observe((state) => state.isPublic);
  const title = plate.observe((state) => state.title);

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleDelete = () => {
    if (currentUserIsOwner) {
      currentUser?.deletePlate(plate);
    }
  };

  const onDeleteAccept = () => {
    handleDelete();
    setDeleteAlertOpen(false);
  };

  const onCleanupAccept = () => {
    plate.cleanupBlocks();
    handleClose();
  };

  return (
    <Modal close={handleClose} title="Plate Settings">
      {!currentUserIsOwner && (
        <>
          <DisabledBox />
          <WarningText>Only the owner of the Plate can update settings.</WarningText>
        </>
      )}
      <Wrapper>
        <StyledTable>
          <tbody>
            <tr>
              <TD isFirstColumn>&lt;title&gt;</TD>
              <TD>{title}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;owner&gt;</TD>
              <TD>
                <Username user={plate.owner} />
              </TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;visibility&gt;</TD>
              <TD>{isPublic ? "Public" : "Private"}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;plateId&gt;</TD>
              <TD>{plate.snapshot.id}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;createdAt&gt;</TD>
              <TD>{plate.snapshot.createdAt.toString()}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;updatedAt&gt;</TD>
              <TD>{plate.snapshot.updatedAt.toString()}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;screenshotTime&gt;</TD>
              <TD>{plate.snapshot.screenshotTime?.toString()}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;blockCount&gt;</TD>
              <TD>{plate.blockCount}</TD>
            </tr>
            <tr>
              <TD isFirstColumn>&lt;likes&gt;</TD>
              <TD>{plate.snapshot.likes.length}</TD>
            </tr>
          </tbody>
        </StyledTable>
        <H2 style={{ marginTop: "20px" }}>Block Actions</H2>
        <PageEditButton onClick={() => setCleanupAlertOpen(true)} half $disabled={!plate.blockCount}>
          Clean Up
        </PageEditButton>
        <Alert
          type="question"
          acceptText="Continue"
          declineText="Cancel"
          onAccept={onCleanupAccept}
          onDecline={() => setCleanupAlertOpen(false)}
          isOpen={cleanupAlertOpen}
          headerText="Clean up blocks?"
          bodyText="WARNING: this will change the order and arrangement of blocks."
        />
        <H2>Plate Actions</H2>
        <PageEditButton onClick={() => setDeleteAlertOpen(true)} $dangerous half>
          Delete
        </PageEditButton>
        <Alert
          type="question"
          acceptText="Delete"
          declineText="Cancel"
          onAccept={onDeleteAccept}
          onDecline={() => setDeleteAlertOpen(false)}
          isOpen={deleteAlertOpen}
          headerText="Are you sure you want to delete this plate?"
          bodyText="Deleting is a permanent action and cannot be undone."
        />
      </Wrapper>
    </Modal>
  );
}
const Wrapper = styled.div`
  width: 100%;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
`;

const WarningText = styled.div`
  color: red;
  padding: 10px 0px;
`;

const DisabledBox = styled.div`
  opacity: 0.5;
  background-color: #e6e6e6;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const PageEditButton = styled(Button)<{ half?: boolean }>`
  padding: 5px 20px 5px 20px;
  width: ${({ half }) => (half ? "50%" : "100%")};
  font-weight: 400;
  margin-bottom: 10px;
  display: flex;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  font-weight: 400;
  font-size: 13px;
`;
